/**
 * NO04 - Ordre Search Component
 */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Paper,
    TextField,
    Button,
    Collapse,
    Typography,
    InputAdornment,
    Alert,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import * as actionsNO04 from '../../../store/actions/indexNO04';
import { NO04SearchParams } from '../../../models/NO04';

const useStyles = makeStyles()((theme) => ({
    searchContainer: {
        padding: theme.spacing(2, 2.5),
        margin: 0,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0,
    },
    quickSearchWrapper: {
        display: 'flex',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        maxWidth: '620px',
    },
    quickSearchField: {
        width: '220px',
        flexShrink: 0,
        backgroundColor: '#f5f5f5',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
            height: '38px',
            fontSize: '0.88rem',
        },
        '& .MuiInputBase-input': {
            padding: '8px 12px',
            fontSize: '0.88rem',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
    },
    searchButton: {
        minWidth: '84px',
        height: '38px',
        textTransform: 'none',
        fontSize: '0.88rem',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    advancedToggle: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.78rem',
        marginTop: theme.spacing(1.25),
        '&:hover': { color: theme.palette.primary.main },
    },
    advancedSection: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginTop: theme.spacing(1),
    },
    // Compact fields/buttons for the advanced section only — same pattern as NO05Search.
    advancedTextField: {
        width: '160px',
        flexShrink: 0,
        backgroundColor: '#f5f5f5',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
            height: '34px',
            fontSize: '0.85rem',
        },
        '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: '0.85rem',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
    },
    advancedButton: {
        minWidth: '72px',
        height: '34px',
        textTransform: 'none',
        fontSize: '0.82rem',
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
    },
    searchModeToggle: {
        '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8rem', padding: theme.spacing(0.5, 1.5) },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            '&:hover': { backgroundColor: '#d7edd9' },
        },
    },
    dateField: {
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            height: '34px',
            fontSize: '0.85rem',
        },
        '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: '0.85rem',
        },
        '& .MuiInputLabel-root': { fontSize: '0.85rem' },
        '& .MuiInputLabel-shrink': { fontSize: '0.85rem' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
    },
}));

const getAdvancedInitial = () => ({
    customerNumber: '',
    orderNumber: '',
    requisitionNumber: '',
    productId: '',
    invoiceCreditNumber: '',
    receiver: '',
    address: '',
    fromDate: '',
    toDate: '',
});

export default function NO04Search() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const loading = useSelector((state: AppState) => state.NO04Ordre.loading);
    const companyId = useSelector((state: AppState) => state.auth.userJWT?.Department ?? '');

    const [advancedOpen, setAdvancedOpen] = useState(false);
    // Each search mode remembers its own text — switching modes shows that mode's own value
    // (or blank if nothing was typed for it yet), and switching back restores it, rather than
    // sharing one field's text across both modes.
    const [quickSearchValues, setQuickSearchValues] = useState<{ order: string; customer: string }>({ order: '', customer: '' });
    const [searchMode, setSearchMode] = useState<'order' | 'customer'>('order');
    const quickSearch = quickSearchValues[searchMode];
    const setQuickSearch = (value: string) => setQuickSearchValues((prev) => ({ ...prev, [searchMode]: value }));
    const [advancedState, setAdvancedState] = useState(getAdvancedInitial);
    const [validationError, setValidationError] = useState('');

    const handleAdvancedChange = (field: string, value: string) => {
        setAdvancedState((prev) => ({ ...prev, [field]: value }));
    };

    const buildParams = (): NO04SearchParams => {
        const base: NO04SearchParams = { companyId };
        if (!advancedOpen) {
            const v = quickSearch.trim();
            if (!v) return base;
            if (searchMode === 'order') base.orderNumber = v;
            else base.customerNumber = v;
            return base;
        }
        if (advancedState.customerNumber.trim()) base.customerNumber = advancedState.customerNumber.trim();
        if (advancedState.orderNumber.trim()) base.orderNumber = advancedState.orderNumber.trim();
        if (advancedState.requisitionNumber.trim()) base.requisitionNumber = advancedState.requisitionNumber.trim();
        if (advancedState.productId.trim()) base.productId = advancedState.productId.trim();
        if (advancedState.invoiceCreditNumber.trim()) base.invoiceCreditNumber = advancedState.invoiceCreditNumber.trim();
        if (advancedState.receiver.trim()) base.receiver = advancedState.receiver.trim();
        if (advancedState.address.trim()) base.address = advancedState.address.trim();
        if (advancedState.fromDate) base.fromDate = advancedState.fromDate;
        if (advancedState.toDate) base.toDate = advancedState.toDate;
        return base;
    };

    const hasSearchParams = (): boolean => {
        if (!advancedOpen) return quickSearch.trim().length > 0;
        return !!(
            advancedState.customerNumber.trim() ||
            advancedState.orderNumber.trim() ||
            advancedState.requisitionNumber.trim() ||
            advancedState.productId.trim() ||
            advancedState.invoiceCreditNumber.trim() ||
            advancedState.receiver.trim() ||
            advancedState.address.trim() ||
            advancedState.fromDate ||
            advancedState.toDate
        );
    };

    const handleSearch = () => {
        if (!hasSearchParams()) {
            setValidationError('Du må fylle inn minst ett søkefelt før du søker.');
            return;
        }
        setValidationError('');
        dispatch(actionsNO04.NO04SearchRequest(buildParams()));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') handleSearch();
        else if (event.key === 'Escape') handleClear();
    };

    const handleClear = () => {
        setQuickSearchValues({ order: '', customer: '' });
        setAdvancedState(getAdvancedInitial());
        setValidationError('');
        dispatch(actionsNO04.NO04ClearResults());
    };

    const handleToggleAdvanced = () => {
        setValidationError('');
        setAdvancedOpen((prev) => !prev);
    };

    const advancedField = (label: string, field: string, placeholder: string, maxLength?: number) => (
        <TextField
            size="small"
            label={label}
            placeholder={placeholder}
            value={(advancedState as any)[field]}
            onChange={(e) => handleAdvancedChange(field, e.target.value)}
            onKeyDown={handleKeyDown}
            className={classes.advancedTextField}
            inputProps={maxLength ? { maxLength } : undefined}
        />
    );

    return (
        <Paper className={classes.searchContainer} elevation={2}>
            {/* Title row */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.75}>
                <Box>
                    <Typography variant="h5" fontWeight={700} lineHeight={1.2}>
                        Mine ordre
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        Søk på ordre siste 5 år
                    </Typography>
                </Box>
                <Tooltip
                    title={
                        <Box sx={{ maxWidth: 420, maxHeight: '70vh', overflowY: 'auto' }}>
                            <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                Programmet brukes for å søke opp alle ordrer som er registrert siste 5 år.
                            </Typography>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>
                                Hurtigsøk - Klikk på hva du ønsker å søke på:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                    <b>Ordrenummer</b> – ved å taste inn ordrenummer får du frem kun denne ene ordren.
                                </Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem' }}>
                                    <b>Kundenummer</b> – ved søk på kundenummer får du alle ordrer på denne kunden i synkende rekkefølge etter dato.
                                </Typography>
                            </Box>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Avansert søk:</Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                Når du åpner avansert søk blankes eventuelt søk fra hurtigsøk ut. Her kan du søke på en eller flere kriterier i kombinasjon.
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mb: 1, pl: 2.5 }}>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Kundenummer</b> – søk på kundenummer</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Ordrenummer</b> – ved å taste inn f.eks 9* her får du kun ordrenummer som starter på 9</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Fra dato</b> – skriv inn dato i format DD.MM.YYYY eller velg fra kalender</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Rekv.nr</b> – brukes alene eller i kombinasjon med andre søkefelter</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Faktura/kreditnummer</b> – søk på faktura eller 9* for kreditnota</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Til dato</b> – skriv inn dato i format DD.MM.YYYY eller velg fra kalender</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Alfa/artikkel</b> – søk på en alfakode eller kombinert med artikkelnummer</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>OM</b> – ordremottaker</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem' }}><b>Vareadresse</b> – søk fra feltene vareadresse 1–4 på ordren</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                Etter inntasting av søkekriterier, klikk på Søk-knappen eller tast Enter.
                            </Typography>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Tabell med ordrer:</Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                Ved søk som gir mer enn én linje vises ordrene i synkende rekkefølge på dato – nyeste ordre øverst.
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Ordrenummer med link til visning av ordren</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Fakturanummer med link til visning av fakturaen</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Dato for opprettelse av ordren</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Kundenummer og kundenavn</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Frakt/sporing vises hvis dette er registrert, hvis ikke vises printernavn</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Rekv.nr fra ordre</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Ordrestatus</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Ordremottaker</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Antall bestilt / antall levert – viser antall i stk., ikke antall varelinjer</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}>Sum ordre uten mva</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem' }}>Bruttofortjeneste på ordren – vises kun for de som har tilgang til å se dette i program TAB1</Typography>
                            </Box>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>Uttrekk til Excel:</Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                Tabellen som vises på skjerm etter dine søkekriterier kan lastes ned i Excel i to ulike varianter:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                <Typography component="li" sx={{ fontSize: '0.85rem', mb: 0.5 }}><b>Excel</b> – tabellen slik du ser på skjerm, med én linje pr. ordre</Typography>
                                <Typography component="li" sx={{ fontSize: '0.85rem' }}><b>Med ordrelinjer</b> – fil med alle ordrelinjer fra valgte utplukk</Typography>
                            </Box>
                        </Box>
                    }
                    placement="bottom-end"
                    arrow
                >
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            userSelect: 'none',
                            flexShrink: 0,
                            mt: -0.5,
                        }}
                    >
                        i
                    </Box>
                </Tooltip>
            </Box>
            {/* Validation error */}
            {validationError && (
                <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.9rem' }} onClose={() => setValidationError('')}>
                    {validationError}
                </Alert>
            )}
            {/* Quick Search (hidden when advanced is open) */}
            {!advancedOpen && (
                <>
                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                    <ToggleButtonGroup
                        value={searchMode}
                        exclusive
                        size="small"
                        onChange={(_, val) => { if (val) setSearchMode(val); }}
                        className={classes.searchModeToggle}
                    >
                        <ToggleButton value="order">Ordrenummer</ToggleButton>
                        <ToggleButton value="customer">Kundenummer</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                <Box className={classes.quickSearchWrapper}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={searchMode === 'order' ? 'Søk på ordrenummer' : 'Søk på kundenummer'}
                        value={quickSearch}
                        onChange={(e) => setQuickSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={classes.quickSearchField}
                        inputProps={{ maxLength: 6 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={handleSearch}
                        disabled={loading}
                        className={classes.searchButton}
                        sx={{ backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' } }}
                    >
                        Søk
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        className={classes.searchButton}
                        title="Nullstill (Escape)"
                        sx={{ borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c' } }}
                    >
                        Nullstill
                    </Button>
                </Box>
                </>
            )}

            {/* Advanced Toggle */}
            <Box className={classes.advancedToggle} onClick={handleToggleAdvanced}>
                {advancedOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {advancedOpen ? 'Tilbake til hurtigsøk' : 'Avansert søk'}
                </Typography>
            </Box>

            {/* Advanced Section — same compact flex-wrap pattern as NO05 */}
            <Collapse in={advancedOpen}>
                <Box className={classes.advancedSection}>
                    {advancedField('Kunde', 'customerNumber', 'F.eks. 500190', 6)}
                    {advancedField('Ordre', 'orderNumber', 'F.eks. 100001', 6)}
                    {advancedField('Rekv.nr.', 'requisitionNumber', 'F.eks. ED23433')}
                    {advancedField('Alfa/Artikkel', 'productId', 'F.eks. 1234567')}
                    {advancedField('Faktura-/kreditnr.', 'invoiceCreditNumber', 'F.eks. CF82904', 6)}
                    {advancedField('OM', 'receiver', 'F.eks. KJ', 2)}
                    {advancedField('Vareadresse', 'address', 'F.eks. Frank')}
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                        <DatePicker
                            label="Fra dato"
                            format="DD.MM.YYYY"
                            value={advancedState.fromDate ? dayjs(advancedState.fromDate) : null}
                            onChange={(date: Dayjs | null) =>
                                handleAdvancedChange('fromDate', date ? date.format('YYYY-MM-DD') : '')
                            }
                            slotProps={{
                                textField: { size: 'small', placeholder: 'Velg dato', className: classes.dateField },
                            }}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                        <DatePicker
                            label="Til dato"
                            format="DD.MM.YYYY"
                            value={advancedState.toDate ? dayjs(advancedState.toDate) : null}
                            onChange={(date: Dayjs | null) =>
                                handleAdvancedChange('toDate', date ? date.format('YYYY-MM-DD') : '')
                            }
                            slotProps={{
                                textField: { size: 'small', placeholder: 'Velg dato', className: classes.dateField },
                            }}
                        />
                    </LocalizationProvider>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={handleSearch}
                        disabled={loading}
                        className={classes.advancedButton}
                        sx={{ backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' } }}
                    >
                        Søk
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        className={classes.advancedButton}
                        sx={{ borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c' } }}
                    >
                        Nullstill
                    </Button>
                </Box>
            </Collapse>
        </Paper>
    );
}
